import { useSelector, shallowEqual, useDispatch } from 'react-redux';

import {
  AI_GENERATED_CONTENT_DISPLAYED,
  EVENT_NAME_ALERT_VIEWED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { log } from 'src/app/utils/logging';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { getLanguageAI } from 'src/app/v1/dataAnalytics/utils/getLanguageAI';
import { actionUpdateHasAIAssistanceBeenUsed } from 'src/app/v1/state/modules/formData/hasAIAssistanceBeenUsed';
import { actionUpdateJobDescription } from 'src/app/v1/state/modules/formData/jobDescription';
import { actionUpdateJobSummary } from 'src/app/v1/state/modules/formData/jobSummary';
import { actionUpdateHasReviewedGeneratedContent } from 'src/app/v1/state/modules/session/hasReviewedGeneratedContent';
import { actionUpdateCajaHasAIAssistanceBeenUsed } from 'src/app/v1/state/modules/tealium/cajaHasAIAssistanceBeenUsed';
import { INPUT_MAX_LENGTH } from 'src/app/v1/views/components/JobSummary';
import {
  type GenerateJobContentInput,
  useGenerateJobContentMutation,
} from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';
import type { LanguageAI } from 'src/types/languageAI';

export const useJobContentGeneration = () => {
  const {
    advertiserId,
    advertiserName,
    categoryText,
    draftId,
    jobDescription,
    jobLocationText,
    jobTitle,
    subCategoryText,
    workType,
  } = useSelector(
    (state: AppState) => ({
      advertiserId: state.session.advertiserId,
      advertiserName: state.session.advertiserName,
      categoryText: state.session.categoryText,
      draftId: state.session.draftId,
      jobDescription: state.formData.jobDescription,
      jobLocationText: state.session.jobLocationText,
      jobTitle: state.formData.jobTitle,
      subCategoryText: state.session.subCategoryText,
      workType: state.formData.workType,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();

  const [generateJobContentMutation] = useGenerateJobContentMutation();

  const generateJobContent = async (locale?: LanguageAI) => {
    const input: GenerateJobContentInput = {
      companyName: advertiserName || '',
      categoryName: categoryText,
      draftId: draftId || '',
      jobTitle,
      locationDescription: jobLocationText,
      priorJobDescription: jobDescription,
      subCategoryName: subCategoryText,
      workType,
      locale,
    };

    try {
      const { data } = await generateJobContentMutation({
        variables: { input },
      });

      if (
        data?.generateJobContent.__typename ===
        'JobContentGenerationResponseSuccessPayload'
      ) {
        const {
          generateJobContent: {
            jobDescription: generatedJobDescription,
            jobSummary: generatedJobSummary,
          },
        } = data;
        dispatch(actionUpdateJobDescription(generatedJobDescription));
        dispatch(actionUpdateJobSummary(generatedJobSummary));

        dispatch(actionUpdateHasAIAssistanceBeenUsed(true));
        dispatch(actionUpdateCajaHasAIAssistanceBeenUsed(true));

        dispatch(actionUpdateHasReviewedGeneratedContent(false));

        getTrackingAdapter.sendLinkEvent({
          eventName: AI_GENERATED_CONTENT_DISPLAYED,
          aiGeneratedSection: 'job description and job summary',
          aiLanguageSelected: getLanguageAI(locale),
        });

        if (generatedJobSummary.length > INPUT_MAX_LENGTH) {
          getTrackingAdapter.sendLinkEvent({
            alertShownMessage: 'Must be between 1 and 150 characters',
            alertShownMessageCode: 'ai generated job summary over char limit',
            alertShownTone: 'critical',
            eventName: EVENT_NAME_ALERT_VIEWED,
            fieldName: 'jobSummary',
            companyName: input.companyName,
            categoryName: input.categoryName,
            draftId: input.draftId,
            jobTitle: input.jobTitle,
            locationDescription: input.locationDescription,
            subCategoryName: input.subCategoryName,
            workType: input.workType,
          });
        }
      }
    } catch (error) {
      log.error(
        'useJobContentGeneration | generateJobContent',
        {
          ...input,
          advertiserId,
          error,
        },
        false,
      );
      throw error;
    }
  };

  return {
    generateJobContent,
  };
};
